import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Filter,
  LayoutDashboard,
  LogOut,
  Menu,
  Send,
  Server as ServerIcon,
  Settings,
  X,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import { useLogout } from '../hooks/useAuth';
import { Logo } from './Logo';

const NAV_MAIN = [
  { to: '/dashboard', label: 'Panoramica', Icon: LayoutDashboard },
  { to: '/servers', label: 'Server', Icon: ServerIcon },
  { to: '/events', label: 'Eventi', Icon: AlertTriangle },
];

const NAV_SETUP = [
  { to: '/channels', label: 'Canali', Icon: Send },
  { to: '/rules', label: 'Regole', Icon: Filter },
  { to: '/settings', label: 'Impostazioni', Icon: Settings },
];

const COLLAPSED_KEY = 'ixam.sidebar.collapsed';

export function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // IXAM: stato persistente — ricordiamo la preferenza dell'utente fra
  // i page reload. Il drawer mobile è una concept separato (non usa
  // collapsed: su mobile o è aperto a tutta larghezza o è chiuso).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  });

  function toggleCollapsed(): void {
    setCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  }

  // IXAM: chiudi il drawer mobile a ogni navigazione.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const initials = (user?.name ?? user?.email ?? '?')
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');

  return (
    <div className="min-h-screen md:flex bg-paper text-ink">
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 bg-ink/50 z-40"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        className={`
          fixed md:static top-0 bottom-0 left-0 z-50
          ${collapsed ? 'md:w-[68px]' : 'md:w-[240px]'}
          w-[260px] flex-shrink-0
          bg-accent-deep text-accent-on-dark
          flex flex-col py-5 px-3
          transform transition-all duration-200 ease-out
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <div className="px-2 pb-4 mb-3 border-b border-white/10 flex items-center justify-between">
          {collapsed ? (
            <Logo size={36} variant="mark" onDark className="md:mx-auto" />
          ) : (
            <Logo size={36} onDark withTagline />
          )}
          <button
            className="md:hidden text-accent-on-dark hover:text-white p-1.5 -mr-1.5 rounded-lg"
            onClick={() => setDrawerOpen(false)}
            aria-label="Chiudi menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {!collapsed && <SideGroupLabel>Operazioni</SideGroupLabel>}
          {NAV_MAIN.map((item) => (
            <SideItem key={item.to} {...item} collapsed={collapsed} />
          ))}

          {!collapsed && <SideGroupLabel>Configurazione</SideGroupLabel>}
          {collapsed && <div className="my-2 border-t border-white/10 mx-1" />}
          {NAV_SETUP.map((item) => (
            <SideItem key={item.to} {...item} collapsed={collapsed} />
          ))}
        </nav>

        {/* IXAM: collapse toggle solo desktop. Su mobile il drawer si gestisce
             dall'header con il bottone Menu. */}
        <button
          onClick={toggleCollapsed}
          className="hidden md:flex items-center justify-center mt-3 mx-1 py-2 text-accent-on-dark-faint hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          aria-label={collapsed ? 'Espandi menu' : 'Comprimi menu'}
          title={collapsed ? 'Espandi menu' : 'Comprimi menu'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        {user && (
          <div
            className={`mt-3 pt-3 border-t border-white/10 px-1 flex items-center ${
              collapsed ? 'justify-center' : 'gap-3 px-2'
            }`}
          >
            <div
              className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-light to-accent flex items-center justify-center text-accent-deep text-xs font-bold flex-shrink-0"
              title={collapsed ? `${user.name} — ${user.role === 'admin' ? 'admin' : 'viewer'}` : undefined}
            >
              {initials}
            </div>
            {!collapsed && (
              <div className="text-xs leading-tight min-w-0">
                <div className="text-white font-semibold truncate">{user.name}</div>
                <div className="text-accent-on-dark-faint truncate">
                  {user.role === 'admin' ? 'admin' : 'viewer'}
                </div>
              </div>
            )}
          </div>
        )}
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-paper-card border-b border-line px-4 md:px-8 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="md:hidden p-2 -ml-2 rounded-lg text-ink-muted hover:text-ink hover:bg-paper-soft"
              onClick={() => setDrawerOpen(true)}
              aria-label="Apri menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="text-sm text-ink-muted truncate">
              <span className="hidden sm:inline">Monitor alert hardware </span>
              <span className="text-ink-faint hidden md:inline">— single-tenant MVP</span>
              <span className="sm:hidden font-semibold text-ink">IXAM</span>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-4">
            {user && (
              <div className="text-right text-sm leading-tight hidden md:block">
                <div className="font-semibold text-ink truncate max-w-[200px]">
                  {user.email}
                </div>
                <div className="text-xs text-ink-faint">Sessione attiva</div>
              </div>
            )}
            <button
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="ixam-btn-ghost"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SideItem({
  to,
  label,
  Icon,
  collapsed,
}: {
  to: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  collapsed: boolean;
}) {
  return (
    <NavLink
      to={to}
      // IXAM: title attribute fornisce il tooltip nativo del browser quando
      // collassato — nessuna libreria extra, accessibile via screen reader,
      // si comporta come ci si aspetta in tutti i browser.
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `group relative flex items-center ${
          collapsed ? 'justify-center px-0' : 'gap-3 px-2.5'
        } py-2 rounded-lg text-sm transition-colors ${
          isActive
            ? 'bg-accent-deep-2 text-white font-semibold'
            : 'text-accent-on-dark hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
      {collapsed && (
        // IXAM: tooltip custom in stile Grenoble — appare a destra dell'icona,
        // animazione opacity, non interferisce con i click. pointer-events-none
        // così il flicker hover->tooltip->fuori non chiude il menu.
        <span className="pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-ink text-white text-xs font-medium rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50">
          {label}
        </span>
      )}
    </NavLink>
  );
}

function SideGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.12em] text-accent-on-dark-faint font-semibold px-2.5 pt-4 pb-1.5">
      {children}
    </div>
  );
}
